import { AccountSubscriptionInfoType } from '@fd/common/subscription';
import { guessCurrency } from './localeToCurrency';
import { datacenter, mobile, ProxyTypeMeta, residential } from './proxyType';
import { findFloorTrafficTier } from './traffic';

export const calculateMultiSubscriptionTotals = (
  subInfo: AccountSubscriptionInfoType
) => {
  const { discountFixed, discountPercentage } = subInfo;
  const mobileTotals = calculateProxyTypeSubtotals({
    trafficGb: subInfo.mobile,
    proxyType: mobile,
    discountFixed,
    discountPercentage,
  });
  const residentialTotals = calculateProxyTypeSubtotals({
    trafficGb: subInfo.residential,
    proxyType: residential,
    discountFixed,
    discountPercentage,
  });
  const dcTotals = calculateProxyTypeSubtotals({
    trafficGb: subInfo.datacenter,
    proxyType: datacenter,
    discountFixed,
    discountPercentage,
  });
  const subtotalPrice =
    mobileTotals.subtotalPrice +
    residentialTotals.subtotalPrice +
    dcTotals.subtotalPrice;
  const totalPrice =
    mobileTotals.totalPrice +
    residentialTotals.totalPrice +
    dcTotals.totalPrice;
  const isDiscounted =
    mobileTotals.tierDiscountPct > 0 ||
    residentialTotals.tierDiscountPct > 0 ||
    dcTotals.tierDiscountPct > 0 ||
    (discountFixed && discountFixed > 0) ||
    (discountPercentage && discountPercentage > 0);
  return {
    subtotalPrice,
    subtotalPriceText: formatCurrency(subtotalPrice),
    totalPrice,
    amount: formatAsCryptomus(totalPrice),
    totalPriceText: formatCurrency(totalPrice),
    residentialTotals,
    mobileTotals,
    dcTotals,
    isDiscounted,
  };
};

type CalculateProxyTypeTotals = {
  trafficGb: number;
  proxyType: ProxyTypeMeta;
  discountFixed?: number;
  discountPercentage?: number;
};
export const calculateProxyTypeSubtotals = (
  options: CalculateProxyTypeTotals
) => {
  const { trafficGb, proxyType, discountPercentage, discountFixed } = options;
  const tier = findFloorTrafficTier(trafficGb);
  const tierDiscountPct = tier.discountPct;
  const tierDiscountMultiplier = pctToMultiplier(tierDiscountPct);

  const subTotalPriceCents = trafficGb * proxyType.priceCents;
  const subtotalPrice = subTotalPriceCents / 100;

  let pricePerGbCents = proxyType.priceCents;
  let discountSource = 'none';
  let discountPct = 0;
  if (discountPercentage && discountPercentage > tierDiscountPct) {
    pricePerGbCents *= pctToMultiplier(discountPercentage);
    discountSource = 'discountPercentage';
    discountPct = discountPercentage;
  } else if (tierDiscountPct > 0) {
    pricePerGbCents *= tierDiscountMultiplier;
    discountSource = 'tier';
    discountPct = tierDiscountPct;
  }

  pricePerGbCents = Math.floor(pricePerGbCents);
  let totalPrice = (trafficGb * pricePerGbCents) / 100;

  if (discountFixed) {
    totalPrice -= discountFixed;
    if (totalPrice < 0) totalPrice = 0;
  }

  return {
    subtotalPrice,
    subtotalPriceText: formatCurrency(subtotalPrice),
    pricePerGbCents, //  Needed for stripe, because stripe price calculation is pricePerGb * quantity
    amount: formatAsCryptomus(totalPrice),
    totalPrice,
    totalPriceText: formatCurrency(totalPrice),

    tierDiscountPct,
    discountSource,
    discountPct,
  };
};
export type TotalsType = ReturnType<typeof calculateProxyTypeSubtotals>;

export const pctToMultiplier = (pct: number) => {
  return (100 - pct) / 100;
};

/**
 *
 * @param usd 123.4
 * @returns "123.40"
 */
export function formatAsCryptomus(usd: number) {
  return Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(usd);
}

export function formatCurrency(usd: number) {
  return (
    '$' +
    Intl.NumberFormat(navigator.language, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(usd)
  );
}

export function formatCurrency2(usd: number) {
  return Intl.NumberFormat(navigator.language, {
    style: 'currency',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    currency: guessCurrency(),
  }).format(usd);
}
